import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { onActiveAlarm, onInActiveAlarms } from "store/actions/polling";
import { updateSystems, updateSystemsConnection } from "store/actions/systemsAndLiveAlarms";
import { updateTags } from "store/actions/tagsList";
import useSystemsDataHandler from "hooks/useSystemsDataHandler";

import { getAlarm } from "api/alarmsList";
import { getRequest } from "api/api";

import getLogger from "utils/logger.util";
import { extractSystemId, formatDate } from "utils/utils";
import API_URLS from "constants/apis.url";
import SYSTEM_STATUS, { CONNECTION_STATUS_CONNECTED } from "constants/systemStatus";

let allActiveAlarms = [];
let systemsAsObj = {};

const WebSocketComponent = () => {
  const Logger = getLogger("WebSocket");
  const dispatch = useDispatch();
  const { systems, poll } = useSelector(state => state);
  const { getSystemState } = useSystemsDataHandler();

  const [isWSOpen, setIsWSOpen] = useState(false);
  const [wsInstance, setWSInstance] = useState(null);
  const [systemsWithWSConnection, setSystemsWithWSConnection] = useState([]);

  //Close Web Socket when component destroy.
  useEffect(() => () => closeWebSocket(), []);
  useEffect(() => (allActiveAlarms = poll.activeAlarms), [poll.activeAlarms]);
  useEffect(() => {
    const { systemsAsObj: sysAsObj } = systems;
    systemsAsObj = sysAsObj;
  }, [systems]);

  const ACTIVE_ALARM = 1;
  const ALARM_KEY = "ALARM";
  const STATUS_KEY = "STATUS";
  const ACTUAL_SYS_STATUS = "actualTAG001";
  const CONNECTION_STATUS_KEY = "ConnectionStatus";
  const ALARMGEN_KEY = "ALARMGEN";
  const TAG001_KEY = "TAG001";

  const TAG_MAPPING = {
    [STATUS_KEY]: STATUS_KEY,
    [TAG001_KEY]: "systemStatus",
    [CONNECTION_STATUS_KEY]: "connectionStatus"
  };

  useEffect(() => {
    const systems = Object.keys(systemsAsObj);
    if (systems.length && (!isWSOpen || isSystemsPermissonChanged(systems))) {
      setIsWSOpen(true);
      initiateNewWSConnection(systems);
    }
  }, [systemsAsObj]);

  const isSystemsPermissonChanged = systemsIDs => {
    let isChanged = systemsIDs.length !== systemsWithWSConnection.length;
    if (!isChanged) {
      const filteredArray = systemsIDs.filter(value => !systemsWithWSConnection.includes(value));
      isChanged = Boolean(filteredArray.length);
    }
    return isChanged;
  };

  const initiateNewWSConnection = async systems => {
    const URL = API_URLS.getPubSubTokenURL;
    const tokenResp = await getRequest(URL, false);
    const subscriptionURL = tokenResp.data.url;
    openWebSocket(subscriptionURL, systems);
  };

  /**
   * Check system status when GEN Alarm on/Off.
   * We need to check status when we are on landing page or inside system's pages.
   *
   * Not checking system state for any system, if navigated inside any other system or
   * System's connection status is Offline(0)
   *
   * @param {*} system
   */
  const checkSystemStatusOnAlarm = system => {
    const currentSystem = extractSystemId();
    if (!currentSystem || currentSystem === system) {
      const { connectionStatus } = systemsAsObj[system];
      Logger.log(`checkSystemStatusOnAlarm connectionStatus for ${system} is ${connectionStatus}`);
      if (connectionStatus === SYSTEM_STATUS.STATUS_ONLINE) {
        const STATUS = systemsAsObj[system].systemStatus;
        setTimeout(() => {
          const updatedData = getSystemUpdatedData(system, { STATUS });
          dispatch(updateSystems({ system, updatedData }));
        }, 500);
      }
    }
  };

  const systemStatusCalculatedVal = (system, connectionStatus, TagValue) => {
    let status = 0;
    if (systemsAsObj[system]) {
      const { actualTAG001 } = systemsAsObj[system];
      TagValue = TagValue || actualTAG001;
      status = calculateSystemStatus(system, +TagValue, connectionStatus);
    }
    return status;
  };

  const getSystemUpdatedData = (system, newData) => {
    const lastActiveTime = formatDate(newData.Time);
    const dynamicProduction = system.includes("XCEL") ? "TAG072" : "TAG071";
    const dynamicConductivity = system.includes("XCEL") ? "TAG089" : "TAG088";
    Object.keys(TAG_MAPPING).forEach(key => {
      if (TAG_MAPPING[key] === "production" || TAG_MAPPING[key] === "conductivity") {
        delete TAG_MAPPING[key];
      }
    });
    TAG_MAPPING[dynamicProduction] = "production";
    TAG_MAPPING[dynamicConductivity] = "conductivity";
    //If we are getting system event's, then system connection status is 1
    const updatedData = {
      lastActiveTime,
      [TAG_MAPPING[CONNECTION_STATUS_KEY]]: "1"
    };
    let connectionStatus;
    for (const tag in TAG_MAPPING) {
      const TagValue = newData[tag];
      if (TagValue) {
        // Calculating Connection status based on key STATUS/ConnectionStatus
        const currentStatus = systemsAsObj[system].connectionStatus;
        Logger.log(`getSystemUpdatedData ${system} - ${tag}:`, TagValue + ", currentStatus: ", currentStatus);
        if (tag === CONNECTION_STATUS_KEY || (tag === STATUS_KEY && currentStatus !== undefined && currentStatus !== TagValue)) {
          let systemConnectionData;
          if (tag === CONNECTION_STATUS_KEY) {
            const { EventType } = TagValue;
            connectionStatus = `${+(EventType === CONNECTION_STATUS_CONNECTED)}`;
            systemConnectionData = { [TAG_MAPPING[tag]]: connectionStatus };
          } else if (tag === STATUS_KEY) {
            systemConnectionData = systemConnectionData || {};
            systemConnectionData[TAG_MAPPING.ConnectionStatus] = TagValue;
            if (!newData[TAG001_KEY] && TagValue !== SYSTEM_STATUS.STATUS_OFFLINE) {
              systemConnectionData[TAG_MAPPING[TAG001_KEY]] = systemStatusCalculatedVal(system, SYSTEM_STATUS.STATUS_ONLINE);
            }
          }
          if (systemConnectionData) {
            systemConnectionData.lastActiveTime = lastActiveTime;
            dispatch(updateSystemsConnection({ system, updatedData: systemConnectionData }));
          }
        }
        // Calculating System status based on key TAG001
        else if (tag === TAG001_KEY) {
          Logger.log(`System status changes for ${system} - ${tag}: ${TagValue}`);
          updatedData[ACTUAL_SYS_STATUS] = TagValue;
          updatedData[TAG_MAPPING[tag]] = systemStatusCalculatedVal(system, connectionStatus, +TagValue);
        } else {
          const dataKey = TAG_MAPPING[tag];
          const existingData = systemsAsObj[system][dataKey];
          if (existingData && typeof existingData === "object") {
            updatedData[dataKey] = { ...existingData, value: TagValue };
          } else {
            updatedData[dataKey] = TagValue;
          }
        }
      }
    }
    return updatedData;
  };

  /**
   * This function is used to calculate System's Status(Idle, Alarming, Offline or Online).
   * If CONNECTION_STATUS is 0, then state will be offline
   * else if  CONNECTION_STATUS  is 1 and system have at least one active alarm(ALARMGEN),
   * then Status will be 2 (Alarming)
   * else If CONNECTION_STATUS is 1 AND (SYSTEM_STATUS is 0, 1 or 9 and
   * there is no active alarms) for that system then, Status will be 0, (Idle)
   * else SYSTEM_STATUS will be 1 (Running/Online)
   *
   * @param {*} system System ID
   * @param {*} status SystemStatus
   * @returns calculated system status
   */
  const calculateSystemStatus = (system, status, newConnectionStatus) => {
    let calculatedStatus = SYSTEM_STATUS.STATUS_OFFLINE;
    if (systemsAsObj) {
      let { connectionStatus, systemStatus } = systemsAsObj[system];
      connectionStatus = newConnectionStatus || connectionStatus;
      status = status || systemStatus;
      calculatedStatus = getSystemState(system, connectionStatus, +status);
    }
    return calculatedStatus;
  };

  /**
   * This function triggered when there is any data recevied on Web Socket.
   * This is will check if there is any alarm data in received object on WS.
   * If Alarms is off, then will remove alarm in redux else if alarms are on,
   * then will fetch alarms details from backend and will updated in redux.
   *
   * @param {*} system
   * @param {*} data
   */
  const updateAlarms = (system, data) => {
    const newAlarms = [];
    const inActiveAlarms = [];
    for (const key in data) {
      if (key.includes(ALARM_KEY)) {
        Logger.log(`Alarm over WebSocket for ${system} - ${key} : ${data[key]}, Time: ${data.Time}`);
        if (+data[key] === ACTIVE_ALARM) {
          newAlarms.push(key);
        } else {
          inActiveAlarms.push(key);
        }
      }
    }
    if (inActiveAlarms.length) {
      dispatch(onInActiveAlarms({ system, inActiveAlarms }));
      const isAnyGenAlarmHandled = inActiveAlarms.some(alarm => alarm.includes(ALARMGEN_KEY));
      if (isAnyGenAlarmHandled) checkSystemStatusOnAlarm(system);
    }
    newAlarms.forEach(alarmId => getAlarmData(system, alarmId, data.Time));
  };

  /**
   * This function is used to get alarms data from server, if already not in active state
   * If Alarm is in active state, will update alarm timestamp with current timestamp.
   *
   * @param {*} sysId
   * @param {*} alarmId
   */
  const getAlarmData = async (sysId, alarmId, Time) => {
    const alarmIndex = allActiveAlarms.findIndex(alarm => sysId === alarm.sysId && alarmId === alarm.alarmId);
    if (alarmIndex === -1) {
      const timestamp = formatDate(Time);
      const alarmDetails = {
        sysId,
        timestamp,
        alarmId,
        readStatus: 0,
        id: alarmId + timestamp + sysId,
        sysName: systemsAsObj[sysId]?.systemName
      };
      getAlarmDescAndUpdateList(alarmDetails);
    }
  };

  const getAlarmDescAndUpdateList = async alarm => {
    const { sysId, alarmId } = alarm;
    const resp = await getAlarm(sysId, alarmId);
    alarm.description = resp.data[0].Description;
    dispatch(onActiveAlarm(alarm));
    if (alarmId.includes(ALARMGEN_KEY)) {
      checkSystemStatusOnAlarm(sysId);
    }
  };

  /**
   * This function is called when there is any data received on WS.
   *
   * @param {*} message
   */
  const onTopic = message => {
    if (!isLogoutPage()) {
      const dataInJson = JSON.parse(message);
      const { group: system, data } = dataInJson;
      //const isDocumentVisible = document.visibilityState === "visible";
      //Update data only when document is visible
      if (system && data && systemsAsObj[system]) {
        const currentSystem = extractSystemId();
        //Logger.log('onTopic - ', system, data);
        updateAlarms(system, data);
        //Update System data only when on landing page i.e. currentSystem is null
        // if (isLandingPage()) {
        const updatedData = getSystemUpdatedData(system, data);
        if (updatedData) {
          dispatch(updateSystems({ system, updatedData }));
        }
        // }

        if (currentSystem && currentSystem === system) {
          dispatch(updateTags(data));
        }
      }
    } else {
      Logger.log("onTopic - Message received on logout page..");
      closeWebSocket();
    }
  };

  const isLogoutPage = () => {
    const { pathname } = window.location;
    return pathname.includes("logout");
  };

  const closeWebSocket = () => {
    Logger.log("closeWebSocket - closing Web Socket connection");
    if (wsInstance) {
      allActiveAlarms = [];
      systemsAsObj = {};
      wsInstance.close();
    }
  };

  const openWebSocket = (url, systems) => {
    //if (systems.length) {
    const ws = new WebSocket(url, API_URLS.azurePubSubProtocal);
    setWSInstance(ws);
    ws.onopen = () => {
      Logger.log("[WebSocket connection opened..");
      topicHandler(ws, "joinGroup", systems);
      setSystemsWithWSConnection(systems);
    };
    ws.onclose = () => {
      Logger.log("[WebSocket connection closed..");
      // topicHandler(ws, 'leaveGroup', systems);
      setSystemsWithWSConnection([]);
    };
    ws.onmessage = message => onTopic(message.data);
    //}
  };

  const topicHandler = (ws, type, systems) => {
    if (ws && systems) {
      systems.forEach((group, ackId) => ws.send(JSON.stringify({ type, group, ackId })));
    }
  };

  return null;
};

export default WebSocketComponent;
